import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FaEllipsisV } from "react-icons/fa";
import {
  ATLAS_BTN_PRIMARY,
  ATLAS_BTN_SECONDARY,
  formatButtonLabel,
} from "./atlasButtonStyles";
import AtlasDropdownMenuItem, {
  resolveDropdownMenuItemVariant,
} from "./AtlasDropdownMenuItem";

const MENU_MIN_WIDTH = 160;
const MENU_ITEM_HEIGHT = 40;
const VIEWPORT_PAD = 8;

const ActionDropdown = ({
  actions = [],
  title,
  trigger = "kebab",
  label = "Actions",
  disabled = false,
  buttonVariant = "secondary",
  menuZIndexClass = "z-[9999]",
  align = "auto",
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    const menuWidth = Math.max(
      MENU_MIN_WIDTH,
      menuEl?.offsetWidth || MENU_MIN_WIDTH,
    );
    const menuHeight = Math.max(
      MENU_ITEM_HEIGHT,
      menuEl?.offsetHeight ||
        actions.length * MENU_ITEM_HEIGHT + (title ? 36 : 12) + 12,
    );

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp =
      spaceBelow < menuHeight + VIEWPORT_PAD && spaceAbove > spaceBelow;

    let top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;

    let left;
    if (align === "right") {
      left = rect.right - menuWidth;
    } else if (align === "left") {
      left = rect.left;
    } else {
      const fitsRight = rect.left + menuWidth <= window.innerWidth - VIEWPORT_PAD;
      const fitsLeft = rect.right - menuWidth >= VIEWPORT_PAD;
      if (fitsRight) left = rect.left;
      else if (fitsLeft) left = rect.right - menuWidth;
      else left = Math.max(VIEWPORT_PAD, window.innerWidth - menuWidth - VIEWPORT_PAD);
    }

    top = Math.max(
      VIEWPORT_PAD,
      Math.min(top, window.innerHeight - menuHeight - VIEWPORT_PAD),
    );
    left = Math.max(
      VIEWPORT_PAD,
      Math.min(left, window.innerWidth - menuWidth - VIEWPORT_PAD),
    );

    setMenuPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    requestAnimationFrame(updateMenuPosition);
  }, [open, actions.length, title, align]);

  useEffect(() => {
    if (!open) return;
    const closeOnScroll = () => setOpen(false);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", updateMenuPosition);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", updateMenuPosition);
    };
  }, [open, actions.length, title, align]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const menu = open ? (
    <div
      ref={menuRef}
      className={`fixed bg-white border border-gray-200 rounded-lg shadow-xl ${menuZIndexClass} overflow-visible p-1.5 min-w-[160px]`}
      style={{
        top: `${menuPos.top}px`,
        left: `${menuPos.left}px`,
        ...(menuZIndexClass && typeof menuZIndexClass === "string" && menuZIndexClass.match(/z-\[(\d+)\]/)
          ? { zIndex: parseInt(menuZIndexClass.match(/z-\[(\d+)\]/)[1], 10) }
          : {}),
      }}
    >
      {title ? (
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-1">
          {formatButtonLabel(title)}
        </div>
      ) : null}
      {actions.map((action, idx) => (
        <AtlasDropdownMenuItem
          key={idx}
          label={action.label}
          variant={resolveDropdownMenuItemVariant(action)}
          disabled={typeof action.onClick !== "function"}
          onClick={() => {
            if (typeof action.onClick === "function") {
              action.onClick();
            }
            setOpen(false);
          }}
        />
      ))}
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          setOpen(!open);
        }}
        disabled={disabled}
        className={
          trigger === "button"
            ? `${buttonVariant === "primary" ? ATLAS_BTN_PRIMARY : ATLAS_BTN_SECONDARY} ${
                disabled ? "opacity-50 cursor-not-allowed" : ""
              }`
            : `p-1.5 rounded-lg transition-colors ${
                disabled
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`
        }
      >
        {trigger === "button" ? formatButtonLabel(label) : <FaEllipsisV size={14} />}
      </button>

      {open && createPortal(menu, document.body)}
    </>
  );
};

export default ActionDropdown;
